import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption } from '@angular/material/core';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { FormControl, UntypedFormControl, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { format, isDate, isValid, parse } from 'date-fns';
import { CommonDateModeEnum, DatePreset } from './date-component.models';
import moment from 'moment';
import { Moment } from 'moment';
import { MatFormField, MatLabel, MatHint, MatSuffix, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';

import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { DATEPICKER_FORMAT } from '../../constants/date/datepicker-formats';
import { defaultStartDatePreset, startDateModePresets } from './start-date-mode';
import { defaultEndDatePreset, endDateModePresets } from './end-date-mode';
import { defaultDatePreset, defaultModePresets } from './default-mode';

export const onDateFormat = 'yyyy-MM-dd';
export const onDateRegExp = /^[BD]([-+]\d{1,3})?$/;

export const defaultOptionalDatePreset = {
    mode: CommonDateModeEnum.none,
    value: null
};

export type DateSelectorFieldMode = 'StartDate' | 'EndDate' | 'default';
export type DateSelectorFieldConfig = FormlyFieldConfig & {
    props: {
        mode?: DateSelectorFieldMode;
    };
};

@Component({
    templateUrl: './date-selector.component.html',
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMAT },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormField,
        MatLabel,
        MatSelect,
        ReactiveFormsModule,
        MatOption,
        MatHint,
        MatInput,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepicker,
        MatError
    ]
})
export class DateSelectorComponent extends FieldType<DateSelectorFieldConfig> implements OnInit, OnDestroy {
    private readonly onDestroy = new Subject<void>();
    private readonly empty: DatePreset = {
        mode: CommonDateModeEnum.none,
        label: '-- None --',
        value: null as any
    };

    public onDateModeControl = new UntypedFormControl();
    public calendarDateControl = new UntypedFormControl(null);
    public customControl!: FormControl<string>;

    constructor() {
        super();
    }

    get presets(): DatePreset[] {
        const _presets: DatePreset[] = this.props.required ? [] : [this.empty];
        switch (this.props.mode) {
            case 'StartDate':
                return [..._presets, ...startDateModePresets];
            case 'EndDate':
                return [..._presets, ...endDateModePresets];
            case 'default':
            default:
                return [..._presets, ...defaultModePresets];
        }
    }

    public get pattern() {
        switch (this.props.mode) {
            case 'StartDate':
                return this.props.pattern ? new RegExp(this.props.pattern) : /.*/;
            case 'EndDate':
                return this.props.pattern ? new RegExp(this.props.pattern) : /.*/;
            case 'default':
            default:
                return onDateRegExp;
        }
    }

    public get defaultPreset() {
        if (!this.props.required) {
            return this.presets.find(preset => preset.mode === defaultOptionalDatePreset.mode);
        }
        let preset: DatePreset;
        switch (this.props.mode) {
            case 'StartDate':
                preset = this.presets.find(preset => preset.mode === defaultStartDatePreset.mode) as DatePreset;
                break;
            case 'EndDate':
                preset = this.presets.find(preset => preset.mode === defaultEndDatePreset.mode) as DatePreset;
                break;
            case 'default':
            default:
                preset = this.presets.find(preset => preset.mode === defaultDatePreset.mode) as DatePreset;
        }
        return preset;
    }

    public get customModeInitialValue() {
        switch (this.props.mode) {
            case 'StartDate':
                return format(new Date(), 'yyyy-MM-dd');
            case 'EndDate':
                return format(new Date(), 'yyyy-MM-dd');
            case 'default':
            default:
                return 'D-5';
        }
    }

    public get patternErrorMessage() {
        const message = 'Value must match';
        switch (this.props.mode) {
            case 'StartDate':
                return `${message} ${this.pattern?.toString()}`;
            case 'EndDate':
                return `${message} ${this.pattern?.toString()}`;
            case 'default':
            default:
                return `${message} D-123 or B-123`;
        }
    }

    ngOnInit() {
        console.debug(this.key);
        console.debug(this.model);
        console.debug(this.formControl);

        this.customControl = new FormControl<string>('', [Validators.required, Validators.pattern(this.pattern)]) as any;

        if (this.props.required) {
            this.configureFormValidators();
        }

        if (this.formControl.value) {
            this.setInitialFormState();
        } else {
            this.onDateModeControl.setValue(this.defaultPreset);
            this.setParentFormControlPreset(this.defaultPreset as any);
        }

        this.initializeValueChangeHandlers();
    }

    ngOnDestroy() {
        this.onDestroy.next();
        this.onDestroy.complete();
    }

    public get mode() {
        return this.onDateModeControl.value as DatePreset;
    }

    public get calendarDateMode() {
        return this.mode.mode === CommonDateModeEnum.calendarDate;
    }

    public get relativeMode() {
        return !this.calendarDateMode;
    }

    public get customMode() {
        return this.mode.mode === CommonDateModeEnum.custom;
    }

    private setParentFormControlPreset(preset: DatePreset) {
        console.debug(`updating parent form with preset`, preset.value);
        this.formControl.setValue(preset.value);
        this.updateValidators(null as any);
    }

    private setParentFormControlCustom(value: string) {
        console.debug(`updating parent form with calendar date`, value);
        this.formControl.setValue(value);
        this.updateValidators([Validators.required, Validators.pattern(this.pattern)]);
    }

    private setParentFormControlCalendarDate(date: Moment) {
        const value = format(date.toDate(), onDateFormat);
        console.debug(`updating parent form with custom date`, value);
        this.formControl.setValue(value);
        this.updateValidators(null as any);
    }

    private updateValidators(validators: ValidatorFn | ValidatorFn[]) {
        setTimeout(() => {
            this.formControl.setValidators(validators);
            this.formControl.updateValueAndValidity();
        });
    }

    private configureFormValidators(): void {
        this.onDateModeControl.addValidators(Validators.required);
        this.calendarDateControl.addValidators(Validators.required);
        this.customControl.addValidators(Validators.required);
    }

    private setInitialFormState(): void {
        const asDate = parse(this.formControl.value, onDateFormat, new Date());
        const match = this.presets.find(preset => preset.value === this.formControl.value);
        if (match) {
            this.onDateModeControl.setValue(match);
        } else if (isDate(asDate) && isValid(asDate)) {
            const preset = this.presets.find(preset => preset.mode === CommonDateModeEnum.calendarDate);
            this.onDateModeControl.setValue(preset);
            this.calendarDateControl.setValue(moment(asDate));
        } else if (this.pattern.test(this.formControl.value)) {
            let preset = this.presets.find(preset => preset.value === this.formControl.value);
            if (!preset) {
                preset = this.presets.find(preset => preset.mode === CommonDateModeEnum.custom);
                this.customControl.setValue(this.formControl.value);
            }
            this.onDateModeControl.setValue(preset);
        } else {
            this.onDateModeControl.setValue(this.defaultPreset);
        }
    }

    private initializeValueChangeHandlers(): void {
        this.onDateModeControl.valueChanges
            .pipe(
                takeUntil(this.onDestroy),
                tap((preset: DatePreset) => {
                    console.debug(preset);
                    if (preset.mode === CommonDateModeEnum.calendarDate) {
                        this.calendarDateControl.setValue(moment());
                    } else if (preset.mode === CommonDateModeEnum.custom) {
                        this.customControl.setValue(this.customModeInitialValue);
                    } else {
                        this.setParentFormControlPreset(preset);
                    }
                })
            )
            .subscribe();

        this.calendarDateControl.valueChanges
            .pipe(
                takeUntil(this.onDestroy),
                tap(value => this.setParentFormControlCalendarDate(value))
            )
            .subscribe();

        this.customControl.valueChanges
            .pipe(
                takeUntil(this.onDestroy),
                tap(value => this.setParentFormControlCustom(value))
            )
            .subscribe();
    }
}
